import { ref, markRaw, onMounted, onUnmounted, type Component, type ConcreteComponent } from 'vue';
import { useMobile } from '@composables/useMobile';
import type { ModalState, ModalStateProps } from '@app/interfaces/modalState.interface';
import type { UseMobileModalReturn } from '@app/interfaces/useMobileModalReturn.interface';

const modalState = ref<ModalState>({
	isOpen: false,
	component: null,
	props: {}
});

type ModalStackEntry = Pick<ModalState, 'component' | 'props'>;
const modalStack = ref<ModalStackEntry[]>([]);

const getClosedState = (): ModalState => ({
	isOpen: false,
	component: null,
	props: {}
});

const syncStateFromStack = (): void => {
	const previous = modalStack.value[modalStack.value.length - 1];

	if (!previous) {
		modalState.value = getClosedState();
		return;
	}

	modalState.value = {
		isOpen: true,
		component: previous.component,
		props: previous.props
	};
};

const resetStack = (): void => {
	modalStack.value = [];
	modalState.value = getClosedState();
};

/**
 * Composable for managing mobile modal state.
 * Provides methods to open/close modals with dynamic components.
 *
 * @returns Modal state and control methods
 *
 * @example
 * const { open, close, modalState } = useMobileModal();
 * open(MyComponent, { title: 'Hello' });
 *
 * @note Props are stored as-is. For optimal memory usage, prefer passing
 * plain objects/primitives rather than large reactive objects (refs, reactive).
 * Always call close() when done to release references.
 */
export const useMobileModal = (): UseMobileModalReturn => {
	const push = (component: Component, props: ModalStateProps = {}): void => {
		modalStack.value.push({
			component: markRaw(component) as ConcreteComponent,
			props
		});

		syncStateFromStack();
	};

	const pop = (): void => {
		if (!modalStack.value.length) {
			return;
		}

		modalStack.value.pop();
		syncStateFromStack();
	};

	return {
		modalState,
		open: push,
		close: pop,
		closeAll: resetStack
	};
};

export const useModalBreakpoint = (): void => {
	let stopMobileWatch: (() => void) | undefined;
	const { onMobileChange } = useMobile();

	onMounted(() => {
		stopMobileWatch = onMobileChange((isMobileNow, wasMobile) => {
			if (!(wasMobile && !isMobileNow && modalState.value.isOpen)) return;

			resetStack();
		});
	});

	onUnmounted(() => {
		if (stopMobileWatch) {
			stopMobileWatch();
			stopMobileWatch = undefined;
		}
	});
};
